import { useState } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { 
  LayoutDashboard, 
  FileText, 
  FileCheck, 
  Users, 
  BarChart3,
  Menu,
  X,
  Truck,
  TrendingUp,
  Globe
} from "lucide-react";

const navKeys = [
  { path: "/", labelKey: "nav.home", icon: LayoutDashboard },
  { path: "/facturas", labelKey: "nav.invoices", icon: FileText },
  { path: "/documentos-soporte", labelKey: "nav.supportDocs", icon: FileCheck },
  { path: "/proveedores", labelKey: "nav.suppliers", icon: Users },
  { path: "/reportes", labelKey: "nav.reports", icon: BarChart3 },
  { path: "/metricas", labelKey: "nav.businessMetrics", icon: TrendingUp },
];

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const toggleLanguage = () => {
    i18n.changeLanguage(i18n.language === 'es' ? 'en' : 'es');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile menu button */}
      <button
        data-testid="mobile-menu-btn"
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-slate-900 text-white rounded-lg"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`fixed left-0 top-0 h-screen w-64 bg-slate-900 z-40 transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Logo */}
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <Truck className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-white font-bold text-lg font-['Barlow']">TransFactura</h1>
              <p className="text-slate-400 text-xs">{t('company.subtitle')}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {navKeys.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.replace("/", "") || "home"}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "bg-white/10 text-white border-l-[3px] border-blue-500 ml-[-3px]"
                    : "text-slate-400 hover:bg-white/5 hover:text-slate-200"
                }`}
                onClick={() => setSidebarOpen(false)}
              >
                <Icon className="w-5 h-5" />
                {t(item.labelKey)}
              </NavLink>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800 space-y-3">
          <button
            onClick={toggleLanguage}
            className="flex items-center justify-center gap-2 w-full py-2 rounded-lg text-xs font-medium transition-colors bg-white/5 hover:bg-white/10 text-slate-300"
          >
            <Globe className="w-3.5 h-3.5" />
            {i18n.language === 'es' ? 'English' : 'Español'}
          </button>
          <div className="text-center text-slate-500 text-xs">
            <p>{t('company.name')}</p>
            <p>{t('company.taxIdLabel')}: {t('company.taxId')}</p>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen">
        <div className="p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
